import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useCrashTrackIncidentInfoQuery } from "../../api/network/adminApiServices";
import svgIcons from "../../assets/svg/svgIcons";
import { useAccountTimezone } from "../../hooks/timezone";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
} from "../../utils/dateTime";
import { convertKmphTo, convertMetersTo } from "../../utils/distance";
import { useLoggedInUserData } from "../../utils/user";
import {
  DISTANCE_UNIT_VALUES,
  DISTANCE_UNITS,
  DISTANCE_UNITS_OPTIONS,
  SPEED_UNIT_VALUES,
  SPEED_UNITS,
  SPEED_UNITS_OPTIONS,
} from "../admin-details/account/constants";
import { crashtrack_tab_slugs } from "./crash-track-admin-details";
import { CrashTrackTabParams } from "./type";
import { APP_CONFIG } from "../../constants/constants";

const ScreenCrashTrackTabIncidentInfo = ({
  assetCrashIdAsset,
  assetCrashIdTimestamp,
  onLoadComplete,
  printMode = false,
}: CrashTrackTabParams) => {
  const tab_slug = crashtrack_tab_slugs[1]; // "incident_info";
  const { t: tCrashTrackDetails } = useTranslation("translation", {
    keyPrefix: `admins.crashtrack.detailsPage`,
  });
  const { t } = useTranslation("translation", {
    keyPrefix: `admins.crashtrack.detailsPage.tab_${tab_slug}`,
  });
  const hasCalledLoadComplete = useRef(false);

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const { tzForDateFns } = useAccountTimezone();

  const orgDistanceUnit = useLoggedInUserData(
    "distanceUnits"
  ) as DISTANCE_UNIT_VALUES;
  const distanceUnit: DISTANCE_UNITS =
    DISTANCE_UNITS_OPTIONS.find((option) => option.value === orgDistanceUnit)
      ?.unit || DISTANCE_UNITS_OPTIONS[0].unit;

  const orgSpeedUnit = useLoggedInUserData("speedUnits") as SPEED_UNIT_VALUES;
  const speedUnit: SPEED_UNITS =
    SPEED_UNITS_OPTIONS.find((option) => option.value === orgSpeedUnit)?.unit ||
    SPEED_UNITS_OPTIONS[0].unit;

  const {
    data: dataCrashTrackIncidentInfo,
    isFetching: isFetchingCrashTrackIncidentInfo,
    isError: isErrorCrashTrackIncidentInfo,
  } = useCrashTrackIncidentInfoQuery(
    { assetId: assetCrashIdAsset, timestamp: assetCrashIdTimestamp },
    { skip: !assetCrashIdAsset || !assetCrashIdTimestamp }
  );
  useEffect(() => {
    if (
      printMode &&
      !isFetchingCrashTrackIncidentInfo &&
      !hasCalledLoadComplete.current
    ) {
      onLoadComplete?.();
      if (APP_CONFIG.DEBUG.ALL)
        console.log(
          "===== CRASH TRACK TAB LOADED",
          tab_slug,
          new Date().getTime()
        );
      hasCalledLoadComplete.current = true;
    }
  }, [printMode, isFetchingCrashTrackIncidentInfo, onLoadComplete]);

  const tableData = useMemo(() => {
    if (isFetchingCrashTrackIncidentInfo || isErrorCrashTrackIncidentInfo)
      return [];
    const preCrashSpeed =
      !!dataCrashTrackIncidentInfo?.pre_crash_speed_limit?.split("/")?.[0]
        ? Math.round(
            convertKmphTo(
              speedUnit,
              parseFloat(
                dataCrashTrackIncidentInfo?.pre_crash_speed_limit?.split(
                  "/"
                )?.[0]
              )
            )
          ) +
          ` ${
            SPEED_UNITS_OPTIONS.find((option) => option.unit === speedUnit)
              ?.label || SPEED_UNITS_OPTIONS[0].label
          }`
        : "-";
    const preCrashSpeedLimit =
      !!dataCrashTrackIncidentInfo?.pre_crash_speed_limit?.split("/")?.[1]
        ? Math.round(
            convertKmphTo(
              speedUnit,
              parseFloat(
                dataCrashTrackIncidentInfo?.pre_crash_speed_limit?.split(
                  "/"
                )?.[1]
              )
            )
          ) +
          ` ${
            SPEED_UNITS_OPTIONS.find((option) => option.unit === speedUnit)
              ?.label || SPEED_UNITS_OPTIONS[0].label
          }`
        : "-";
    const timeLapsedChunks = !!dataCrashTrackIncidentInfo?.elapsed_time
      ? dataCrashTrackIncidentInfo?.elapsed_time.split(":")
      : [];
    const timeLapsed =
      timeLapsedChunks.length > 0
        ? [
            !!timeLapsedChunks[0] ? `${timeLapsedChunks[0]} hours` : "",
            !!timeLapsedChunks[1] ? `${timeLapsedChunks[1]} minutes` : "",
            !!timeLapsedChunks[2] ? `${timeLapsedChunks[2]} seconds` : "",
          ].filter((item) => !!item)
        : [];
    return [
      {
        label: t("tableFields.gforce"),
        value: dataCrashTrackIncidentInfo?.g_force || "-",
      },
      {
        label: t("tableFields.pre_crash_speed_limit"),
        value: `${preCrashSpeed} / ${preCrashSpeedLimit}`,
      },
      {
        label: t("tableFields.event_datatime"),
        value:
          !!dataCrashTrackIncidentInfo?.event_time && !!tzForDateFns
            ? formattedDateTimeBasedOnTimezone(
                dbTimeToJsTime(
                  parseInt(dataCrashTrackIncidentInfo?.event_time || "0")
                ),
                tzForDateFns
              )
            : `${dataCrashTrackIncidentInfo?.event_time}`,
      },
      {
        label: t("tableFields.address"),
        value: dataCrashTrackIncidentInfo?.address || "-",
      },
      {
        label: t("tableFields.last_ign_on"),
        value:
          !!dataCrashTrackIncidentInfo?.last_ignition_on && !!tzForDateFns
            ? formattedDateTimeBasedOnTimezone(
                dbTimeToJsTime(
                  dataCrashTrackIncidentInfo?.last_ignition_on || 0
                ),
                tzForDateFns
              )
            : `${dataCrashTrackIncidentInfo?.event_time}`,
      },
      {
        label: t("tableFields.elapsed_time"),
        value: timeLapsed.length > 0 ? timeLapsed.join(" ") : "-",
      },
      {
        label: t("tableFields.miles_driven"),
        value: !!dataCrashTrackIncidentInfo?.miles_driven_since_ign_on
          ? convertMetersTo(
              distanceUnit,
              parseFloat(
                `${dataCrashTrackIncidentInfo?.miles_driven_since_ign_on || 0}`
              ) * 1000
            )
              ?.toFixed(2)
              .replace(".00", "") +
            ` ${
              DISTANCE_UNITS_OPTIONS.find(
                (option) => option.unit === distanceUnit
              )?.label || DISTANCE_UNITS_OPTIONS[0].label
            }`
          : "-",
      },
      {
        label: t("tableFields.miles_driven_last24h"),
        value: dataCrashTrackIncidentInfo?.miles_driven_last_24_hours
          ? convertMetersTo(
              distanceUnit,
              parseFloat(
                `${dataCrashTrackIncidentInfo?.miles_driven_last_24_hours || 0}`
              ) * 1000
            )
              ?.toFixed(2)
              .replace(".00", "") +
            ` ${
              DISTANCE_UNITS_OPTIONS.find(
                (option) => option.unit === distanceUnit
              )?.label || DISTANCE_UNITS_OPTIONS[0].label
            }`
          : "-",
      },
    ];
  }, [
    thisUserOrganizationId,
    dataCrashTrackIncidentInfo,
    isFetchingCrashTrackIncidentInfo,
    isErrorCrashTrackIncidentInfo,
    speedUnit,
  ]);

  if (isFetchingCrashTrackIncidentInfo)
    return (
      <div className="flex-grow flex items-center justify-center min-h-64">
        <img src={svgIcons.LoadingAnimation} alt="" className="size-12" />
      </div>
    );

  if (isErrorCrashTrackIncidentInfo) {
    return (
      <div className="flex flex-col items-center justify-center min-h-64 text-center">
        <p className="font-bold text-xl text-gray-400 px-8">
          {tCrashTrackDetails("tab_data_fetch_error")}
        </p>
      </div>
    );
  }

  return (
    <div className="printable-landscape-page flex flex-col gap-4 p-4">
      <h2 className="font-semibold text-lg">{t("heading")}</h2>
      <div className="flex flex-col bg-white rounded-lg">
        {tableData.map((tableDatum, i) => (
          <div
            className={`flex px-7 py-4${i > 0 ? " border-t border-t-border-gray" : ""}`}
            key={i}
          >
            <div className="min-w-64 max-w-64 font-semibold">
              {tableDatum.label}
            </div>
            <div className="flex-grow">{tableDatum.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
export default ScreenCrashTrackTabIncidentInfo;
